import { useEffect, useState, useRef } from "react";
import { Navbar } from "@/components/Navbar";
import { FileUpload } from "@/components/FileUpload";
import { SavedAlertsPanel } from "@/components/SavedAlertsPanel";
import { ReferralPanel } from "@/components/ReferralPanel";
import { ChefHireInbox } from "@/components/ChefHireInbox";
import { MultiCityPopular } from "@/components/MultiCityPopular";
import { NotificationPrefs } from "@/components/NotificationPrefs";
import { CookBookingsTable, StatusPill } from "@/components/CookBookingsTable";
import { FavoritesPanel } from "@/components/FavoritesPanel";
import { WeekendPicks } from "@/components/WeekendPicks";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { shouldNotify } from "@/lib/notifPrefs";

export { StatusPill };

export default function CookDashboard() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(null);
  const bookingSnapshotRef = useRef(new Map());

  const load = async () => {
    const { data } = await api.get("/bookings/mine");
    if (bookingSnapshotRef.current.size > 0 && typeof Notification !== "undefined" && Notification.permission === "granted" && shouldNotify("bookings")) {
      data.forEach((b) => {
        const prev = bookingSnapshotRef.current.get(b.id);
        if (prev && prev !== b.status && ["approved", "paid"].includes(b.status)) {
          try {
            new Notification(`Booking ${b.status}`, {
              body: `${b.kitchen_name} · ${new Date(b.start_time).toLocaleDateString()}`,
              tag: `booking-${b.id}-${b.status}`,
              icon: "/favicon.ico",
            });
          } catch (err) { console.error("Notification failed:", err); }
        }
      });
    }
    bookingSnapshotRef.current = new Map(data.map((b) => [b.id, b.status]));
    setBookings(data);
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const setLicense = async (file) => {
    await api.post("/cooks/license", { license_url: file.path });
    toast.success("License uploaded and verified");
    refresh();
  };

  const pay = async (booking_id) => {
    setBusy(booking_id);
    try {
      const useCredit = (user?.credit_balance || 0) > 0
        && window.confirm(`Apply your $${user.credit_balance.toFixed(2)} credit to this booking?`);
      const { data } = await api.post("/payments/checkout", {
        booking_id,
        origin_url: window.location.origin,
        use_credit: useCredit,
      });
      if (data.applied_credit > 0) {
        toast.info(`$${data.applied_credit.toFixed(2)} credit applied — charging $${data.charge_amount.toFixed(2)}`);
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const cancel = async (booking_id) => {
    if (!window.confirm("Cancel this booking? If paid, you'll be refunded.")) return;
    try {
      const { data } = await api.post(`/bookings/${booking_id}/cancel`, { reason: "" });
      toast.success(data.refunded ? "Cancelled & refunded" : "Cancelled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    }
  };

  const TABS = [
    { key: "bookings", label: "Bookings" },
    { key: "favorites", label: "Favorites" },
    { key: "alerts", label: "Alerts" },
    { key: "hires", label: "Hire requests" },
    { key: "referrals", label: "Share & earn" },
    { key: "settings", label: "Settings" },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="tick-label">Cook</div>
        <h1 className="font-display font-black text-5xl tracking-tighter mt-2">Your kitchen access.</h1>

        {/* License card */}
        <div className="mt-10 border hairline p-8">
          <div className="tick-label">Food handler license</div>
          <div className="mt-3 flex items-center justify-between gap-6 flex-wrap">
            <div>
              {user?.license_url ? (
                <div>
                  <a
                    data-testid="license-view"
                    href={fileUrl(user.license_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#FF3B30] underline"
                  >View uploaded license</a>
                  <p className="text-neutral-500 text-sm mt-1">Upload a new file to replace.</p>
                </div>
              ) : (
                <p className="text-neutral-400">You must upload your license before booking a kitchen.</p>
              )}
            </div>
            <FileUpload
              testId="license-upload"
              label={user?.license_url ? "Replace license" : "Upload license"}
              accept="image/*,application/pdf"
              onUploaded={setLicense}
            />
          </div>
        </div>

        {/* Tab strip */}
        <div className="mt-12 border-b hairline flex flex-wrap" data-testid="cook-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`cook-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 tick-label transition-colors border-b-2 -mb-px ${tab === t.key ? "border-[#FF3B30] text-white" : "border-transparent text-neutral-500 hover:text-white"}`}
            >{t.label}</button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "bookings" && (
            <>
              <WeekendPicks />
              <CookBookingsTable bookings={bookings} busy={busy} onPay={pay} onCancel={cancel} />
            </>
          )}
          {tab === "favorites" && <FavoritesPanel />}
          {tab === "alerts" && (
            <div className="space-y-6">
              <SavedAlertsPanel />
              <MultiCityPopular />
            </div>
          )}
          {tab === "hires" && <ChefHireInbox />}
          {tab === "referrals" && <ReferralPanel />}
          {tab === "settings" && <NotificationPrefs />}
        </div>
      </div>
    </div>
  );
}
